'use client';

import { useEffect, useRef } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import {
  COOKIE_CONSENT_CHANGED_EVENT,
  consentAllowsGoogleTags,
  parseStoredCookieConsent,
} from '@/lib/analytics/cookie-consent';
import { COOKIE_CONSENT_STORAGE_KEY } from '@/lib/analytics/gtm';
import { isPageTitleReady, pushGtmPageView } from '@/lib/analytics/page-tags';

/**
 * Sends a dataLayer virtual_page_view on each public route, including client
 * navigations. GTM should fire the GA4 page_view tag on the custom event
 * `virtual_page_view`, and the GA4 configuration tag should not also send a
 * page view on load. Private areas (admin, auth, dashboards) are not tagged.
 */
export function GtmPageTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastSent = useRef('');
  const previousLocation = useRef('');

  useEffect(() => {
    const search = searchParams.toString();
    const key = `${pathname}?${search}`;
    const initialTitle = document.title;
    const startedAt = performance.now();
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const send = (title: string) => {
      const stored = parseStoredCookieConsent(localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY));
      if (!consentAllowsGoogleTags(stored)) {
        return;
      }
      if (lastSent.current === key) {
        return;
      }
      const pageLocation = pushGtmPageView({
        pathname: pathname ?? '/',
        searchParams,
        title,
        referrer: previousLocation.current || document.referrer,
      });
      if (pageLocation) {
        lastSent.current = key;
        previousLocation.current = pageLocation;
      }
    };

    const waitForTitle = () => {
      if (cancelled) {
        return;
      }
      const elapsed = performance.now() - startedAt;
      if (!isPageTitleReady(initialTitle, document.title, elapsed)) {
        timer = setTimeout(waitForTitle, 50);
        return;
      }
      send(document.title);
    };

    waitForTitle();
    const onConsent = () => {
      send(document.title);
    };
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, onConsent);

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, onConsent);
    };
  }, [pathname, searchParams]);

  return null;
}
